import { useMutation, useQuery, useQueryClient } from 'react-query';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { PagedResponse } from '@bandido/shared/dtos';

export interface FollowUpRule {
  id: string;
  name: string;
  description?: string | null;
  scanWorkflow: string;
  scanStep?: string | null;
  scanStatus?: string | null;
  scanStatuses?: string[] | null;
  nextWorkflow?: string | null;
  nextStep?: string | null;
  nextStatus?: string | null;
  runOnThisTime: string[];
  daysOfTheWeek: string[];
  weekOfTheMonths?: string[] | null;
  isActive: boolean;
  customField: Record<string, any> | null;
  sendWhatsapp?: boolean;
}

export interface FollowUpFormValues {
  name: string;
  description?: string;
  scanWorkflow: string;
  scanStep?: string;
  scanStatuses: string[];
  nextWorkflow?: string;
  nextStep?: string;
  nextStatus?: string;
  fieldBlockId: string;
  fieldName: string;
  offsetDirection: 'before' | 'after';
  offsetAmount: number;
  offsetUnit: 'hours' | 'days';
  templateId: string;
  templateParams?: string;
  templateMessageText?: string;
  isActive: boolean;
  sendWhatsapp: boolean;
}

interface ListParams {
  agencyId: number;
  page?: number;
  limit?: number;
}

const type = 'case_trigger';

export const useFollowUpRules = ({ agencyId, page = 1, limit = 50 }: ListParams) => {
  return useQuery({
    queryKey: [QueryKeys.getCaseTriggers, { agencyId, page, limit }],
    queryFn: async () => {
      const response = await client.get<PagedResponse<FollowUpRule>>('/scheduled-tasks', {
        params: { agencyId, page, limit, type },
      });
      return response.data;
    },
    enabled: Boolean(agencyId),
  });
};

export const useSaveFollowUpRule = (agencyId: number, ruleId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FollowUpFormValues) => {
      const normalizedNextStatus = payload.nextStatus?.trim() || '';
      const customField = {
        fieldBlockId: payload.fieldBlockId,
        fieldName: payload.fieldName,
        offsetDirection: payload.offsetDirection,
        offsetAmount: payload.offsetAmount,
        offsetUnit: payload.offsetUnit,
        templateId: payload.templateId || undefined,
        templateParams: payload.templateParams ? payload.templateParams.split(',').map((p) => p.trim()).filter(Boolean) : [],
        templateMessageText: payload.templateMessageText || '',
      };

      const body = {
        name: payload.name,
        description: payload.description,
        scanWorkflow: payload.scanWorkflow,
        scanStep: payload.scanStep || undefined,
        scanStatus: payload.scanStatuses.length > 0 ? payload.scanStatuses[0] : '',
        scanStatuses: payload.scanStatuses,
        nextWorkflow: payload.nextWorkflow || undefined,
        nextStep: payload.nextStep || undefined,
        nextStatus: normalizedNextStatus,
        runOnThisTime: ['00:00'],
        daysOfTheWeek: ['monday'],
        weekOfTheMonths: undefined,
        customField,
        isActive: payload.isActive,
        type,
        agencyId,
        sendWhatsapp: payload.sendWhatsapp,
      };

      if (ruleId) {
        const { agencyId: _omitAgencyId, ...updateBody } = body;
        await client.put(`/scheduled-tasks/${ruleId}`, updateBody);
      } else {
        await client.post('/scheduled-tasks', body);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCaseTriggers]);
    },
  });
};

export const useDeleteFollowUpRule = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.delete(`/scheduled-tasks/${id}`, {
        params: { agencyId, type },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCaseTriggers]);
    },
  });
};
